/* debugLog.js
 * Usage examples
 *  debugLog('plain message');                       // always prints in-browser
 *  debugLog(2, 'parsed data', obj);                 // prints only if DEBUG_LEVEL < 2
 *  debugLog(4, 'GROUP steps', { … });               // prints unless
 *                                                  //   └─ DEBUG_LEVEL ≥ 4   OR
 *                                                  //   └─ "steps" is in DEBUG_EXCLUDE_GROUPS
 */

const toNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const DEBUG_LEVEL_DEFAULT = 0;

// Grab env / global settings once at module load
const DEBUG_LEVEL_ENV =
  typeof process !== 'undefined'
    ? toNumber(process.env.DEBUG_LEVEL)               // Node / bundler env
    : DEBUG_LEVEL_DEFAULT;

const EXCLUDE_GROUPS_ENV = (() => {
  const raw =
    (typeof process !== 'undefined' &&
      process.env.DEBUG_EXCLUDE_GROUPS) /* ex: "steps,widgets" */ || '';
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
})();

/**
 * Smart browser-only logger.
 * @param  {...any} args  Anything you’d normally pass to console.log.
 *
 * Rules:
 *   1. If the **first** arg is an integer N, treat it as the log-level.
 *        - Log only when   DEBUG_LEVEL_ENV < N
 *   2. If the **second** arg starts with `"GROUP "` and the group name
 *      (exact match after the word GROUP) is in DEBUG_EXCLUDE_GROUPS,
 *      suppress the log entirely.
 *   3. In all other cases, log exactly what you passed in (including the
 *      numeric level when present).
 */
export default function debugLog(...args) {
  const isBrowser = typeof window !== 'undefined';
  if (!isBrowser) return; // keep server / build logs silent like before

  let shouldLog = true;

  // ---------- level filtering ----------
  if (Number.isInteger(args[0])) {
    const msgLevel = args[0];
    if (DEBUG_LEVEL_ENV >= msgLevel) {
      // Env level is same or higher → suppress
      shouldLog = false;
    }

    // ---------- group exclusion ----------
    if (
      shouldLog &&
      typeof args[1] === 'string' &&
      args[1].startsWith('GROUP ')
    ) {
      const groupName = args[1].slice(6).trim(); // after "GROUP "
      if (EXCLUDE_GROUPS_ENV.includes(groupName)) {
        shouldLog = false;
      }
    }
  }

  if (shouldLog) {
    // still include the numeric level (if present) in the output
    if (process.env.NODE_ENV !== "production") console.log("[DBG]", ...args);
  }
}
